import { type KanbanSolicitacao, type KanbanStatus } from '@/core/api/sicom-api/servicos/kanban/kanban-servicos.model';
import { type ItemStatus } from '@/features/servicos/shared/models/item-status.model';
import { DIAS_PARA_EX_OFFICIO, aptoAExOfficio } from '@/features/servicos/shared/utils/ex-officio.presenter';

/**
 * O prazo e a aptidão ao ex officio vêm do presenter da regra, e são reexportados daqui só para os
 * consumidores do quadro não precisarem saber de dois módulos.
 *
 * Havia uma segunda cópia da conta aqui dentro, com o parser errado — ela lia `dd/MM/yyyy` como
 * `MM/dd/yyyy` e apresentava uma empresa de 15 dias como apta há três meses. Duas implementações da
 * mesma regra é como o filtro do quadro passa a mostrar um conjunto de empresas e o botão de
 * efetivar a aceitar outro.
 */
export { DIAS_PARA_EX_OFFICIO, aptoAExOfficio };

/**
 * Filtro de coluna do quadro: o que cada área oferece e como o card é avaliado.
 *
 * Fora do componente porque o que se decide aqui é regra, não desenho: quais perguntas fazem sentido
 * em cada coluna e o que responde "sim" para um card. Isso se testa sem montar o quadro inteiro.
 */

/** Pergunta que uma coluna pode fazer sobre seus cards. */
export enum ECampoFiltro {
    /** Situação do protocolo no portal (A/T/P). */
    SITUACAO = 'situacao',
    /** Cadastro ou registro. */
    MODALIDADE = 'modalidade',
    /** Pagamento e anuidade em dia — "dá para efetivar". */
    PAGAMENTO = 'pagamento',
    /** Resposta do diretor técnico indicado. Só pessoa jurídica. */
    INDICACAO = 'indicacao',
    /** Analista com o card. */
    RESPONSAVEL = 'responsavel',
    /** Criticidade escolhida no movimento. */
    CRITICIDADE = 'criticidade',
    /** Dias sem movimentação — o que ficou para trás. */
    PARADO = 'parado',
    /** O card tem anotação escrita. */
    ANOTACAO = 'anotacao',
    /** Dias desde a entrada do protocolo, para quem ainda não foi puxado. */
    ESPERA = 'espera',
    /** O médico já foi avisado da pendência. */
    AVISO = 'aviso',
    /** Efetivado ou cancelado. */
    DESFECHO = 'desfecho',
    /** Data em que a solicitação foi concluída. */
    CONCLUSAO = 'conclusao',
    /** Empresa apta à inscrição ex officio: parada além do prazo e sem quitar. */
    EX_OFFICIO = 'exOfficio'
}

/** Estado dos filtros de UMA coluna. Ausente = filtro não aplicado. */
export interface FiltroDaColuna {
    situacao?: string;
    modalidade?: string;
    pagamento?: 'em-dia' | 'pendente';
    indicacao?: string;
    responsavel?: number;
    criticidade?: number;
    /** Mínimo de dias sem movimentação. */
    paradoDias?: number;
    anotacao?: 'com' | 'sem';
    /** Mínimo de dias desde a entrada do protocolo. */
    esperaDias?: number;
    aviso?: 'avisado' | 'sem-aviso';
    /** Mínimo de dias desde o último aviso. */
    avisoDias?: number;
    desfecho?: 'efetivado' | 'cancelado';
    /** Conclusão a partir desta data (ISO `aaaa-mm-dd`). */
    concluidoDe?: string;
    /** Conclusão até esta data (ISO `aaaa-mm-dd`). */
    concluidoAte?: string;
    /** Só as empresas aptas à inscrição ex officio. */
    exOfficio?: boolean;
}

const FILTRO_VAZIO: FiltroDaColuna = {};

/** Códigos de situação do portal que aparecem no quadro. */
export const SITUACOES_DO_QUADRO = ['A', 'T', 'P'] as const;

/**
 * Quais perguntas a coluna oferece, decidido pelas FLAGS dela — nunca pelo nome.
 *
 * A coluna de entrada é o caso que justifica a função existir: ali o card normalmente ainda não tem
 * registro local, então responsável, criticidade e "parado há" viriam vazios para quase tudo. Em
 * vez de oferecer filtro que não responde, ela troca por perguntas que o portal sabe responder.
 */
export function camposDaColuna(coluna: KanbanStatus, exibirIndicacao: boolean, ofereceExOfficio = false): ECampoFiltro[] {
    if (coluna.conclusivo) {
        return [ECampoFiltro.DESFECHO, ECampoFiltro.CONCLUSAO, ECampoFiltro.RESPONSAVEL];
    }

    // O ex officio atravessa as colunas vivas — backlog, em atendimento e pendência —, porque a
    // empresa apta pode estar em qualquer uma delas: o que a qualifica é o tempo parada e a dívida,
    // não onde o card está. Na coluna conclusiva não faz sentido: ali o desfecho já ocorreu.
    const exOfficio = ofereceExOfficio ? [ECampoFiltro.EX_OFFICIO] : [];

    if (coluna.iniciaAtendimento) {
        const campos = [...exOfficio, ECampoFiltro.PAGAMENTO, ECampoFiltro.ESPERA, ECampoFiltro.MODALIDADE, ECampoFiltro.SITUACAO];
        return exibirIndicacao ? [...campos, ECampoFiltro.INDICACAO] : campos;
    }

    if (coluna.avisaPendencia) {
        return [...exOfficio, ECampoFiltro.AVISO, ECampoFiltro.SITUACAO, ECampoFiltro.RESPONSAVEL, ECampoFiltro.PARADO, ECampoFiltro.CRITICIDADE];
    }

    return [...exOfficio, ECampoFiltro.RESPONSAVEL, ECampoFiltro.CRITICIDADE, ECampoFiltro.PARADO, ECampoFiltro.ANOTACAO, ECampoFiltro.MODALIDADE];
}

/** Algum filtro aplicado. Usado para marcar o ícone da coluna e liberar o "Limpar". */
export function filtroAtivo(filtro: FiltroDaColuna | undefined): boolean {
    if (!filtro) {
        return false;
    }
    return Object.values(filtro).some((valor) => valor !== undefined && valor !== null && valor !== '');
}

/**
 * Tempo decorrido, em períodos de 24h, para os filtros de "parado há" e "avisado há".
 *
 * Continua local e continua sendo subtração de instantes porque aqui a pergunta é de duração, e as
 * datas são do SICOM (ISO, com hora). Não serve para o prazo de inércia do ex officio: aquele é
 * decurso de prazo em dias de CALENDÁRIO, contado sobre um campo que vem do portal em três formatos
 * — e é `diasDesde` do `ex-officio.presenter` que sabe lê-los.
 */
function diasDesde(valor: string | undefined, agora: number): number | null {
    if (!valor) {
        return null;
    }
    const marco = new Date(valor).getTime();
    if (Number.isNaN(marco)) {
        return null;
    }
    return Math.floor((agora - marco) / 86_400_000);
}

/** Só a parte da data (`aaaa-mm-dd`), para comparar período sem depender de fuso. */
function apenasData(valor: string | undefined): string {
    return (valor ?? '').slice(0, 10);
}

function atendeSituacao(item: ItemStatus, esperada?: string): boolean {
    return !esperada || (item.situacaoCodigo ?? '').trim().toUpperCase() === esperada;
}

function atendePagamento(item: ItemStatus, esperado?: FiltroDaColuna['pagamento']): boolean {
    if (!esperado) {
        return true;
    }
    // "Em dia" exige as DUAS coisas: protocolo pago e anuidade sem pendência — é a mesma condição
    // que libera efetivar. Qualquer uma delas em falta cai em "pendente".
    const emDia = item.inPago === true && item.isAnuidadePaga !== false;
    return esperado === 'em-dia' ? emDia : !emDia;
}

function atendeDesfecho(item: ItemStatus, esperado?: FiltroDaColuna['desfecho']): boolean {
    if (!esperado) {
        return true;
    }
    const codigo = (item.situacaoCodigo ?? '').trim().toUpperCase();
    return esperado === 'cancelado' ? codigo === 'C' : codigo === 'E';
}

/**
 * O card passa pelo filtro da coluna.
 *
 * `solicitacao` é o registro local e pode faltar — card nunca movido não tem. Quando falta, os
 * filtros que dependem dele NÃO deixam o card passar: quem pergunta "responsável X" não quer de
 * volta o card sem responsável nenhum.
 *
 * @param agora referência de "hoje" em milissegundos; recebida de fora para o teste não depender do relógio.
 */
export function cardAtendeFiltro(item: ItemStatus, solicitacao: KanbanSolicitacao | undefined, filtro: FiltroDaColuna | undefined, agora: number): boolean {
    const alvo = filtro ?? FILTRO_VAZIO;

    if (!atendeSituacao(item, alvo.situacao) || !atendePagamento(item, alvo.pagamento) || !atendeDesfecho(item, alvo.desfecho)) {
        return false;
    }

    if (alvo.exOfficio && !aptoAExOfficio(item, agora)) {
        return false;
    }

    if (alvo.modalidade && (item.modalidadeInscricao ?? '').trim().toUpperCase() !== alvo.modalidade) {
        return false;
    }

    if (alvo.indicacao && (item.indicacaoDiretorTecnico ?? '') !== alvo.indicacao) {
        return false;
    }

    if (alvo.esperaDias !== undefined) {
        const dias = diasDesde(item.ultimaTramitacao?.dataTramitacao, agora);
        if (dias === null || dias < alvo.esperaDias) {
            return false;
        }
    }

    if (alvo.responsavel !== undefined && solicitacao?.idWebUsuarioResponsavel !== alvo.responsavel) {
        return false;
    }

    if (alvo.criticidade !== undefined && solicitacao?.idCriticidade !== alvo.criticidade) {
        return false;
    }

    if (alvo.paradoDias !== undefined) {
        const dias = diasDesde(solicitacao?.dtUltimaMovimentacao, agora);
        if (dias === null || dias < alvo.paradoDias) {
            return false;
        }
    }

    if (alvo.anotacao) {
        const tem = (solicitacao?.totalAnotacoes ?? 0) > 0;
        if (alvo.anotacao === 'com' ? !tem : tem) {
            return false;
        }
    }

    if (alvo.aviso) {
        const avisado = !!solicitacao?.dtUltimoAviso;
        if (alvo.aviso === 'avisado' ? !avisado : avisado) {
            return false;
        }
    }

    if (alvo.avisoDias !== undefined) {
        const dias = diasDesde(solicitacao?.dtUltimoAviso, agora);
        if (dias === null || dias < alvo.avisoDias) {
            return false;
        }
    }

    const conclusao = apenasData(solicitacao?.dtConclusao);
    if (alvo.concluidoDe && (!conclusao || conclusao < alvo.concluidoDe)) {
        return false;
    }
    if (alvo.concluidoAte && (!conclusao || conclusao > alvo.concluidoAte)) {
        return false;
    }

    return true;
}
